import { App } from "../ui/App.ts";
import { Location } from "../types.ts";
import { reverseGeocode } from "../utils/geocoding.ts";
import { t } from "../i18n.ts";

declare const L: any; // Leaflet

export class MapController {
  private app: App;
  public location: Location = {
    lat: 55.6761,
    lon: 12.5683,
    radius: 5,
    isSet: false,
  };
  private leafletMap: any = null;
  private mapMarker: any = null;
  private mapCircle: any = null;

  constructor(app: App) {
    this.app = app;
  }

  setupMapTab() {
    const mapContainer = document.getElementById("leaflet-map");
    if (!mapContainer) return;

    const radiusSlider = document.getElementById("map-radius") as HTMLInputElement;
    const radiusValue = document.getElementById("map-radius-value");
    if (radiusSlider) {
      radiusSlider.addEventListener("input", (e: any) => {
        this.location.radius = parseInt(e.target.value);
        if (radiusValue) radiusValue.textContent = `${e.target.value} km`;
        this.updateMapCircle();
      });
    }

    const searchInput = document.getElementById("map-search") as HTMLInputElement;
    let searchTimeout: any;
    if (searchInput) {
      searchInput.addEventListener("input", (e: any) => {
        clearTimeout(searchTimeout);
        const query = e.target.value.trim();
        if (query.length < 2) return;

        searchTimeout = setTimeout(async () => {
          try {
            const response = await fetch(
              `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=1`,
            );
            const data = await response.json();
            if (data.features && data.features.length > 0) {
              const feature = data.features[0];
              const lat = feature.geometry.coordinates[1];
              const lon = feature.geometry.coordinates[0];
              this.setMapLocation(lat, lon);
              if (this.leafletMap) {
                this.leafletMap.setView([lat, lon], 13);
              }
            }
          } catch (err) {
            console.error("Search error:", err);
          }
        }, 500);
      });
    }

    const applyBtn = document.getElementById("apply-map-location");
    if (applyBtn) {
      applyBtn.addEventListener("click", () => this.applyMapLocation());
    }

    const editOnMapBtn = document.getElementById("edit-on-map-btn");
    if (editOnMapBtn) {
      editOnMapBtn.addEventListener("click", () => {
        this.app.switchTab("map");
      });
    }
  }

  initLeafletMap() {
    if (this.leafletMap) return;

    const mapContainer = document.getElementById("leaflet-map");
    if (!mapContainer) return;

    this.leafletMap = L.map("leaflet-map").setView([this.location.lat, this.location.lon], 12);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(this.leafletMap);

    this.leafletMap.on("click", (e: any) => {
      this.setMapLocation(e.latlng.lat, e.latlng.lng);
    });

    this.updateMapMarker();
    this.updateMapCircle();
  }

  setMapLocation(lat: number, lon: number) {
    this.location.lat = lat;
    this.location.lon = lon;
    this.location.isSet = true;

    const latEl = document.getElementById("map-lat");
    const lonEl = document.getElementById("map-lon");
    if (latEl) latEl.textContent = lat.toFixed(4);
    if (lonEl) lonEl.textContent = lon.toFixed(4);

    this.updateMapMarker();
    this.updateMapCircle();
  }

  updateMapMarker() {
    if (!this.leafletMap) return;

    if (this.mapMarker) {
      this.mapMarker.setLatLng([this.location.lat, this.location.lon]);
    } else {
      this.mapMarker = L.marker([this.location.lat, this.location.lon], {
        draggable: true,
      }).addTo(this.leafletMap);

      this.mapMarker.on("dragend", (e: any) => {
        const pos = e.target.getLatLng();
        this.setMapLocation(pos.lat, pos.lng);
      });
    }
  }

  updateMapCircle() {
    if (!this.leafletMap) return;

    const radiusMeters = (this.location.radius || 5) * 1000;

    if (this.mapCircle) {
      this.mapCircle.setLatLng([this.location.lat, this.location.lon]);
      this.mapCircle.setRadius(radiusMeters);
    } else {
      this.mapCircle = L.circle([this.location.lat, this.location.lon], {
        radius: radiusMeters,
        color: "#3713ec",
        fillColor: "#3713ec",
        fillOpacity: 0.1,
        weight: 2,
      }).addTo(this.leafletMap);
    }
  }

  async applyMapLocation() {
    this.app.showLoading(true, t("loading.finding", this.app.currentLang));

    const geocodeData = await reverseGeocode(this.location.lat, this.location.lon);
    if (geocodeData) {
      const cityInput = document.getElementById("city") as HTMLInputElement;
      const countryInput = document.getElementById("country") as HTMLInputElement;
      if (cityInput && geocodeData.city) cityInput.value = geocodeData.city;
      if (countryInput && geocodeData.country) countryInput.value = geocodeData.country;
    }

    const distanceSlider = document.getElementById("distance") as HTMLInputElement;
    const distanceValue = document.getElementById("distance-value");
    if (distanceSlider && this.location.radius) {
      distanceSlider.value = this.location.radius.toString();
    }
    if (distanceValue && this.location.radius) {
      distanceValue.textContent = `${this.location.radius} km`;
    }

    this.updateCoordsDisplay(this.location.lat, this.location.lon);

    this.app.showLoading(false);

    this.app.switchTab("generate");

    setTimeout(() => {
      this.app.generatePoster();
    }, 100);
  }

  updateCoordsDisplay(lat: number, lon: number) {
    const coordsDisplay = document.getElementById("coords-display");
    const displayLat = document.getElementById("display-lat");
    const displayLon = document.getElementById("display-lon");

    if (coordsDisplay) {
      coordsDisplay.classList.remove("hidden");
    }
    if (displayLat) {
      displayLat.textContent = lat.toFixed(4);
    }
    if (displayLon) {
      displayLon.textContent = lon.toFixed(4);
    }

    if (!this.location.isSet) {
      this.location.lat = lat;
      this.location.lon = lon;

      const mapLat = document.getElementById("map-lat");
      const mapLon = document.getElementById("map-lon");
      if (mapLat) mapLat.textContent = lat.toFixed(4);
      if (mapLon) mapLon.textContent = lon.toFixed(4);

      if (this.leafletMap) {
        this.updateMapMarker();
        this.updateMapCircle();
      }
    }
  }
}
